import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  TableSortLabel,
} from '@mui/material';
import { Edit2, Trash2 } from 'lucide-react';
import { Customer } from '../../types/customer';
import {
  formatCustomerName,
  formatCustomerEmail,
  formatCustomerPhone,
} from '../../utils/customerUtils';

interface CustomerTableProps {
  customers: Customer[];
  onEdit: (customer: Customer) => void;
  onDelete: (id: number) => void;
  onSelect: (customer: Customer) => void;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  onSort: (column: string) => void;
}

export default function CustomerTable({
  customers,
  onEdit,
  onDelete,
  onSelect,
  sortBy,
  sortOrder,
  onSort,
}: CustomerTableProps) {
  const theme = useTheme();

  const createSortHandler = (column: string) => () => onSort(column);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ px: 3, py: 1 }}>
              <TableSortLabel
                active={sortBy === 'firstName'}
                direction={sortOrder === 'ASC' ? 'asc' : 'desc'}
                onClick={createSortHandler('firstName')}
                sx={{ color: theme.palette.text.secondary }}
              >
                Name
              </TableSortLabel>
            </TableCell>

            <TableCell sx={{ py: 1 }}>
              <TableSortLabel
                active={sortBy === 'phone'}
                direction={sortOrder === 'ASC' ? 'asc' : 'desc'}
                onClick={createSortHandler('phone')}
                sx={{ color: theme.palette.text.secondary }}
              >
                Phone
              </TableSortLabel>
            </TableCell>

            <TableCell sx={{ py: 1 }}>
              <TableSortLabel
                active={sortBy === 'email'}
                direction={sortOrder === 'ASC' ? 'asc' : 'desc'}
                onClick={createSortHandler('email')}
                sx={{ color: theme.palette.text.secondary }}
              >
                Email
              </TableSortLabel>
            </TableCell>

            <TableCell align="right" sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {customers.map(customer => (
            <TableRow
              key={customer.id}
              hover
              onClick={() => onSelect(customer)}
              sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell sx={{ fontSize: 16, px: 3 }}>{formatCustomerName(customer)}</TableCell>
              <TableCell>{formatCustomerPhone(customer)}</TableCell>
              <TableCell>{formatCustomerEmail(customer)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, px: 3 }}>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      onEdit(customer);
                    }}
                    sx={{ mr: 2 }}
                  >
                    <Edit2 size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={e => {
                      e.stopPropagation();
                      onDelete(customer.id);
                    }}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
